/** Course 2+2 strict swings + alternation, ported from the frozen research kernel.
 * Price geometry only: no corridor, ranking, signals, writes or network I/O.
 */
import type { CanvasResponse, DisplayBar } from './types';

export const SWING_PERIODS = ['daily', 'weekly', 'monthly'] as const;
export interface SwingTurn {
  kind: 'high' | 'low';
  logical: number;
  price: number;
  confirmedLogical: number;
  stableKnownLogical: number | null;
}
export interface SwingSegment {
  from: { logical: number; price: number };
  to: { logical: number; price: number };
  state: 'stable' | 'provisional' | 'tail';
}
export interface SwingPath {
  status: 'ready' | 'empty' | 'unavailable';
  reason?: 'unsupported_period' | 'unverified_calendar' | 'insufficient_closed_bars';
  turns: SwingTurn[];
  segments: SwingSegment[];
  closedBars: number;
  first: string | null;
  last: string | null;
  breaks: number;
  ambiguousBars: number;
  historyScope?: CanvasResponse['history_scope'];
}
const empty = (reason?: SwingPath['reason']): SwingPath => ({
  status: reason ? 'unavailable' : 'empty', reason, turns: [], segments: [],
  closedBars: 0, first: null, last: null, breaks: 0, ambiguousBars: 0,
});
const valid = (b: DisplayBar) => [b.o, b.h, b.low, b.c].every(Number.isFinite)
  && b.low > 0 && b.h >= Math.max(b.o, b.c) && b.low <= Math.min(b.o, b.c);

export function swingPathForCanvas(data: Pick<CanvasResponse, 'bars' | 'period' | 'recognition' | 'history_scope'>): SwingPath {
  if (!(SWING_PERIODS as readonly string[]).includes(data.period)) return empty('unsupported_period');
  if (!data.recognition?.calendar_verified) return empty('unverified_calendar');
  const closed = new Set(data.recognition.closed_times);
  const input = data.bars.map((bar, logical) => ({ bar, logical }))
    .filter(x => closed.has(x.bar.t) && !x.bar.forming);
  if (input.length < 5) return { ...empty('insufficient_closed_bars'), closedBars: input.length };
  const result = empty();
  result.closedBars = input.length;
  result.historyScope = data.history_scope;
  result.first = input[0].bar.t; result.last = input[input.length - 1].bar.t;
  // Incomplete/invalid bars and calendar gaps cannot create a synthetic adjacency.
  const groups: typeof input[] = [];
  let group: typeof input = [];
  const missing = [...data.recognition.missing_sessions].sort();
  const hasMissing = (left: string, right: string, inclusive: boolean) => {
    let lo = 0, hi = missing.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (inclusive ? missing[mid] < left : missing[mid] <= left) lo = mid + 1;
      else hi = mid;
    }
    return lo < missing.length && (inclusive ? missing[lo] <= right : missing[lo] < right);
  };
  for (const x of input) {
    const previous = group[group.length - 1];
    const days = x.bar.src_days ?? [x.bar.t];
    const hasInternalGap = hasMissing(days[0], days[days.length - 1], true);
    const hasGap = previous && (x.logical !== previous.logical + 1
      || hasMissing(previous.bar.t, x.bar.t, false));
    if (!valid(x.bar) || hasInternalGap || hasGap) {
      if (group.length) groups.push(group);
      group = []; result.breaks++;
    }
    if (valid(x.bar) && !hasInternalGap) group.push(x);
  }
  if (group.length) groups.push(group);
  // One fixed reference for the loaded history: appending future bars cannot rescale old decisions.
  const unit = input.find(x => valid(x.bar))?.bar.c ?? 1;
  const norm = (p: number) => Number((p / unit).toFixed(12));
  for (const block of groups) {
    const raw: Array<SwingTurn & { canonical: number }> = [];
    const dual = new Set<number>();
    for (let i = 2; i < block.length - 2; i++) {
      const others = [i - 2, i - 1, i + 1, i + 2];
      const high = norm(block[i].bar.h), low = norm(block[i].bar.low);
      const isHigh = others.every(j => norm(block[j].bar.h) < high);
      const isLow = others.every(j => norm(block[j].bar.low) > low);
      if (isHigh && isLow) dual.add(block[i].logical);
      for (const kind of ['high', 'low'] as const) {
        if (kind === 'high' ? !isHigh : !isLow) continue;
        const canonical = kind === 'high' ? high : low;
        raw.push({ kind, logical: block[i].logical, price: canonical * unit, canonical,
          confirmedLogical: block[i + 2].logical, stableKnownLogical: null });
      }
    }
    const turns: typeof raw = [];
    for (const point of raw) {
      const previous = turns[turns.length - 1];
      if (previous?.kind === point.kind) {
        const more = point.kind === 'high' ? point.canonical > previous.canonical : point.canonical < previous.canonical;
        if (more) turns[turns.length - 1] = point;
      } else {
        if (previous) previous.stableKnownLogical = point.confirmedLogical;
        turns.push(point);
      }
    }
    result.ambiguousBars += dual.size;
    result.turns.push(...turns.map(({ canonical: _canonical, ...p }) => p));
    for (let i = 1; i < turns.length; i++) {
      const from = turns[i - 1], to = turns[i];
      // A daily OHLC cannot establish the intrabar order of a dual high/low.
      if (dual.has(from.logical) || dual.has(to.logical)) continue;
      result.segments.push({ from, to, state: to.stableKnownLogical == null ? 'provisional' : 'stable' });
    }
    const last = turns[turns.length - 1];
    const finalBlock = block === groups[groups.length - 1];
    if (last && !dual.has(last.logical) && finalBlock) {
      const end = block[block.length - 1];
      // A forming bar may extend the dotted observation tail, never confirm a pivot.
      const displayedLast = data.bars[data.bars.length - 1];
      const tail = end.logical === data.bars.length - 2 && displayedLast.forming && valid(displayedLast)
        ? { bar: displayedLast, logical: data.bars.length - 1 } : end;
      if (tail.logical > last.logical) result.segments.push({ from: last,
        to: { logical: tail.logical, price: tail.bar.c }, state: 'tail' });
    }
  }
  result.status = result.segments.length ? 'ready' : 'empty';
  return result;
}
